/**
 * Deciding what a bulk-guide pick actually is before anything is uploaded.
 *
 * Modelled on JobSeekersImport's `selectFiles`: the rules — accepted type, per-file size, and
 * whether this is a document pick or a single zip — are stated here, out of the component, so they
 * can be tested without a browser or a file picker, and so a rejected file carries the reason it
 * was set aside rather than being silently dropped.
 *
 * A zip and a set of loose documents are two different server routes, so a mixed pick is not a
 * thing we can honour: the partition names which route a pick is for, or refuses it.
 */

export const DOC_ALLOWED_EXT = ['.pdf', '.docx', '.txt'] as const;
export const ZIP_EXT = '.zip';

/** The documents route caps each file at 15MB (MAX_FILE_BYTES in the BFF). */
export const DOC_MAX_BYTES = 15 * 1024 * 1024;
/**
 * A zip can carry many files, so it is allowed to be larger: up to the zip route's own cap
 * (MAX_ZIP_BYTES in backend/src/routes/guideImports.ts). This said 100MB while the server refused
 * anything over 60MB, so a 70MB archive passed the picker and then failed after the upload.
 * test/guideZipLimit.test.ts keeps the two equal.
 */
export const ZIP_MAX_BYTES = 60 * 1024 * 1024;

export type RejectReason = 'type' | 'size' | 'empty';

export interface RejectedFile {
  name: string;
  reason: RejectReason;
}

export type SelectionKind = 'documents' | 'zip' | 'empty';

export interface FileSelection {
  kind: SelectionKind;
  /** Accepted documents (kind === 'documents'). */
  files: File[];
  /** The single accepted zip (kind === 'zip'). */
  zip: File | null;
  rejected: RejectedFile[];
  /** Set when a pick cannot be honoured as a whole (mixed zip + docs, or several zips). */
  error?: string;
}

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

const isZip = (file: File) => extensionOf(file.name) === ZIP_EXT;

/**
 * Partition a picked set into what will be uploaded and what will not.
 *
 * A pick is a zip pick the moment it contains a zip: a zip is expanded server-side into many jobs,
 * so pairing it with loose files would mean two routes for one submit. One zip with nothing else is
 * accepted as a zip; a zip beside documents, or more than one zip, is refused as a whole with a
 * reason, rather than quietly importing half of what was chosen.
 */
export function partitionImportFiles(
  input: File[],
  opts: { docMaxBytes?: number; zipMaxBytes?: number } = {},
): FileSelection {
  const docMaxBytes = opts.docMaxBytes ?? DOC_MAX_BYTES;
  const zipMaxBytes = opts.zipMaxBytes ?? ZIP_MAX_BYTES;
  const files = Array.from(input);

  if (files.length === 0) {
    return { kind: 'empty', files: [], zip: null, rejected: [] };
  }

  const zips = files.filter(isZip);

  if (zips.length > 0) {
    if (zips.length > 1) {
      return { kind: 'empty', files: [], zip: null, rejected: [], error: 'Upload one zip at a time.' };
    }
    if (files.length > 1) {
      return {
        kind: 'empty',
        files: [],
        zip: null,
        rejected: [],
        error: 'Upload a zip on its own, or loose documents without a zip, not both at once.',
      };
    }
    const zip = zips[0];
    if (zip.size === 0) return { kind: 'empty', files: [], zip: null, rejected: [{ name: zip.name, reason: 'empty' }] };
    if (zip.size > zipMaxBytes) return { kind: 'empty', files: [], zip: null, rejected: [{ name: zip.name, reason: 'size' }] };
    return { kind: 'zip', files: [], zip, rejected: [] };
  }

  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  for (const file of files) {
    if (!DOC_ALLOWED_EXT.includes(extensionOf(file.name) as (typeof DOC_ALLOWED_EXT)[number])) {
      rejected.push({ name: file.name, reason: 'type' });
      continue;
    }
    if (file.size === 0) {
      rejected.push({ name: file.name, reason: 'empty' });
      continue;
    }
    if (file.size > docMaxBytes) {
      rejected.push({ name: file.name, reason: 'size' });
      continue;
    }
    accepted.push(file);
  }

  if (accepted.length === 0) {
    return { kind: 'empty', files: [], zip: null, rejected };
  }
  return { kind: 'documents', files: accepted, zip: null, rejected };
}

/** Human copy for a rejection, for the discarded-files note. */
export function rejectReasonLabel(reason: RejectReason, maxBytes = DOC_MAX_BYTES): string {
  switch (reason) {
    case 'type':
      return 'not a PDF, DOCX or TXT';
    case 'size':
      return `over ${Math.round(maxBytes / 1024 / 1024)} MB`;
    case 'empty':
      return 'empty file';
  }
}

/** The `accept` attribute for the file input: documents and a zip. */
export const IMPORT_ACCEPT = [...DOC_ALLOWED_EXT, ZIP_EXT].join(',');
